<template>
  <section ref="mapSection">
    <div
      v-if="isWebGLSupported"
      id="map-restaurent"
      class="map-container relative h-[500px] md:h-[600px]"
    />

    <div
      v-else-if="hasCheckedWebGL"
      class="message-container flex flex-col items-center justify-center h-[500px] text-center"
    >
      <p class="text-red-500 font-bold mb-4">
        WebGL or Hardware Acceleration is not enabled. Follow the instructions
        below to enable it:
      </p>
      <ul class="text-gray-700 text-left">
        <li v-if="isChrome" class="mb-2">
          1. Open Chrome Settings: <strong>chrome://settings/</strong><br />
          2. Go to <strong>Advanced > System</strong><br />
          3. Enable
          <strong>"Use hardware acceleration when available"</strong>.<br />
          4. Restart your browser.
        </li>
        <li v-if="isFirefox" class="mb-2">
          1. Open Firefox Configuration: <strong>about:config</strong><br />
          2. Search for
          <strong>"layers.acceleration.force-enabled"</strong><br />
          3. Set it to <strong>true</strong>.<br />
          4. Restart your browser.
        </li>
        <li v-if="isEdge" class="mb-2">
          1. Open Edge Settings: <strong>edge://settings/</strong><br />
          2. Navigate to <strong>System and performance</strong><br />
          3. Enable
          <strong>"Use hardware acceleration when available"</strong>.<br />
          4. Restart your browser.
        </li>
        <li v-if="!isChrome && !isFirefox && !isEdge" class="mb-2">
          Please refer to your browser's documentation to enable WebGL or
          hardware acceleration.
        </li>
      </ul>
      <p class="text-gray-500 mt-4">
        Once enabled, refresh the page to view the map.
      </p>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from "vue";
import "mapbox-gl/dist/mapbox-gl.css";

const location = {
  address: "123 Restaurant Street, 57000 Metz, France",
  hours: "Open daily: 11:00 AM - 10:00 PM",
  phone: "+33 1 23 45 67 89",
  coordinates: {
    lat: 48.6932648,
    lng: 6.1829989,
  },
};

const mapSection = ref(null);
const isWebGLSupported = ref(false);
const hasCheckedWebGL = ref(false);
const isChrome = ref(false);
const isFirefox = ref(false);
const isEdge = ref(false);
const map = ref(null);
const marker = ref(null);
const popup = ref(null);
let observer = null;

const initMap = async () => {
  await nextTick();

  const container = document.getElementById("map-restaurent");
  if (!container || container.clientHeight === 0) {
    console.error("Map container not found or has no dimensions");
    return;
  }

  // Block pointer events until map projection is ready
  container.style.pointerEvents = "none";

  const mapboxgl = (await import("mapbox-gl")).default;

  const config = useRuntimeConfig();
  mapboxgl.accessToken = config.public.mapboxAccessToken;

  try {
    map.value = new mapboxgl.Map({
      container: "map-restaurent",
      style: "mapbox://styles/mapbox/outdoors-v12",
      center: [6.1829989, 48.6932648],
      zoom: 16,
      trackResize: true,
      fadeDuration: 0,
    });

    // Re-enable pointer events only after map is fully idle (projection matrix ready)
    map.value.once("idle", () => {
      container.style.pointerEvents = "auto";
    });

    map.value.on("load", () => {
      map.value.resize();

      map.value.addControl(new mapboxgl.NavigationControl(), "top-right");
      map.value.addControl(new mapboxgl.ScaleControl(), "bottom-right");

      marker.value = new mapboxgl.Marker({
        draggable: false,
        offset: [0, 10],
        anchor: "bottom",
      })
        .setLngLat([6.1829989, 48.6932648])
        .addTo(map.value);

      popup.value = new mapboxgl.Popup({
        closeButton: true,
        closeOnClick: false,
        closeOnMove: false,
        offset: 20,
        anchor: "bottom",
        className: "custom-popup",
      }).setHTML(`
        <div class="p-4">
          <div class="flex justify-between items-start mb-2">
            <h3 class="text-lg font-semibold text-gray-600">
              Restaurant Location
            </h3>
          </div>
          <div class="space-y-2">
            <p class="text-gray-600">
              <svg class="w-5 h-5 inline-block mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              ${location.address}
            </p>
            <p class="text-gray-600">
              <svg class="w-5 h-5 inline-block mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              ${location.hours}
            </p>
            <p class="text-gray-600">
              <svg class="w-5 h-5 inline-block mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              ${location.phone}
            </p>
          </div>
          <div class="w-full flex items-center justify-center">
            <a
                href="https://www.google.com/maps/dir/?api=1&destination=${location.coordinates.lat},${location.coordinates.lng}"
                target="_blank"
                rel="noopener noreferrer"
                class="mt-4 inline-flex items-center px-4 py-2 bg-(--main-color-700) text-white rounded-lg hover:bg-(--main-color-800) transition-colors"
            >
                <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2">
                    <path d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                Get Directions
            </a>
          </div>
        </div>
      `);

      marker.value.getElement().addEventListener("click", () => {
        popup.value.setLngLat(marker.value.getLngLat()).addTo(map.value);
      });
    });
  } catch (error) {
    console.error("Error initializing map:", error);
  }
};

const checkWebGLAndAcceleration = () => {
  const isWebGLAvailable = () => {
    try {
      const canvas = document.createElement("canvas");
      return !!(
        window.WebGLRenderingContext &&
        (canvas.getContext("webgl") || canvas.getContext("experimental-webgl"))
      );
    } catch {
      return false;
    }
  };

  const isHardwareAccelerationEnabled = () => {
    try {
      const canvas = document.createElement("canvas");
      const gl =
        canvas.getContext("webgl") || canvas.getContext("experimental-webgl");

      if (gl) {
        const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
        if (!debugInfo) return true;
        const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

        if (renderer.toLowerCase().includes("software")) {
          return false;
        }
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  isWebGLSupported.value =
    isWebGLAvailable() && isHardwareAccelerationEnabled();
  hasCheckedWebGL.value = true;

  const userAgent = navigator.userAgent.toLowerCase();
  isChrome.value = userAgent.includes("chrome") && !userAgent.includes("edg");
  isFirefox.value = userAgent.includes("firefox");
  isEdge.value = userAgent.includes("edg");
};

onUnmounted(() => {
  if (map.value) {
    map.value.remove();
    map.value = null;
  }
  if (observer) {
    observer.disconnect();
  }
});

onMounted(() => {
  checkWebGLAndAcceleration();

  if (isWebGLSupported.value && mapSection.value) {
    observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          // Wait for next frame to ensure container is painted with dimensions
          requestAnimationFrame(() => {
            initMap();
          });
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(mapSection.value);
  }
});
</script>

<style scoped>
.message-container {
  background-color: #f8f9fa;
  border: 3px dashed #ddd;
  border-radius: 16px;
  padding: 16px;
}

ul li {
  list-style-type: decimal;
  margin-left: 1.5rem;
}

:deep(.custom-popup) {
  .mapboxgl-popup-content {
    border-radius: 0.5rem;
    box-shadow:
      0 10px 15px -3px rgb(0 0 0 / 0.1),
      0 4px 6px -4px rgb(0 0 0 / 0.1);
    padding: 0;
    width: 300px;
  }

  .mapboxgl-popup-tip {
    border-top-color: rgb(229 231 235);
  }

  .mapboxgl-popup-close-button {
    font-size: 1.5rem;
    padding: 0.5rem;
    color: rgb(107 114 128);
    transition: color 0.2s;
  }

  .mapboxgl-popup-close-button:hover {
    color: rgb(55 65 81);
    background: transparent;
  }

  svg {
    color: rgb(107 114 128);
  }
}

:deep(.dark .custom-popup) {
  .mapboxgl-popup-tip {
    border-top-color: rgb(55 65 81);
  }

  .mapboxgl-popup-close-button {
    color: rgb(156 163 175);
  }

  .mapboxgl-popup-close-button:hover {
    color: rgb(229 231 235);
  }

  svg {
    color: rgb(255 255 255);
  }
}
</style>
